import { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { getProductPrice } from '../../utils/pricing';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../context/AuthContext';
import UpgradeModal from '../common/UpgradeModal';
import IngredientManager from '../ingredients/IngredientManager';

export default function ProductForm({ editingProduct, onEditComplete }) {
    const { addProduct, updateProduct, products, ingredients } = useAppContext();
    const { userProfile } = useAuth();
    const { canAddProduct, getProductLimit } = usePermissions();

    const [name, setName] = useState('');
    const [type, setType] = useState('artesanal');
    const [unitCost, setUnitCost] = useState('');
    const [marginPercent, setMarginPercent] = useState('50');
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    // Campos para produtos artesanais com ingredientes
    const [useIngredients, setUseIngredients] = useState(false);
    const [productIngredients, setProductIngredients] = useState([]);
    const [yieldQuantity, setYieldQuantity] = useState('1');

    // Novos campos para embalagens
    const [sellByPackage, setSellByPackage] = useState(false);
    const [packageSize, setPackageSize] = useState('10');
    const [useCustomPackagePrice, setUseCustomPackagePrice] = useState(false);
    const [customPackagePrice, setCustomPackagePrice] = useState('');
    const [packageTypes, setPackageTypes] = useState([
        { name: 'Unidade', quantity: 1, enabled: true },
        { name: 'Bandeja', quantity: 10, enabled: false },
    ]);

    // Campos para venda por cento
    const [sellByCento, setSellByCento] = useState(false);
    const [centoPrice, setCentoPrice] = useState('');

    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const nameRef = useRef(null);

    useEffect(() => {
        if (nameRef.current) nameRef.current.focus();
    }, []);

    // Carregar dados do produto para edição
    useEffect(() => {
        if (editingProduct) {
            setName(editingProduct.name || '');
            setType(editingProduct.type || 'artesanal');
            setUnitCost(editingProduct.unitCost?.toString() || '');
            setMarginPercent(editingProduct.marginPercent?.toString() || '50');
            setSellByPackage(editingProduct.sellByPackage || false);
            setPackageSize(editingProduct.packageSize?.toString() || '10');
            setUseCustomPackagePrice(editingProduct.useCustomPackagePrice || false);
            setCustomPackagePrice(editingProduct.customPackagePrice?.toString() || '');
            setSellByCento(editingProduct.sellByCento || false);
            setCentoPrice(editingProduct.centoPrice?.toString() || '');
            setUseIngredients(editingProduct.useIngredients || false);
            setProductIngredients(editingProduct.productIngredients || []);
            setYieldQuantity(editingProduct.yieldQuantity?.toString() || '1');
        }
    }, [editingProduct]);

    // Atualizar packageTypes quando packageSize muda
    useEffect(() => {
        const size = Number(packageSize) || 10;
        setPackageTypes([
            { name: 'Unidade', quantity: 1, enabled: true },
            { name: 'Bandeja', quantity: size, enabled: sellByPackage },
        ]);
    }, [packageSize, sellByPackage]);

    // Calcular custo automaticamente baseado nos ingredientes
    useEffect(() => {
        if (useIngredients && productIngredients.length > 0 && yieldQuantity) {
            const totalCost = productIngredients.reduce((sum, pi) => {
                const ingredient = ingredients.find(i => i.id === pi.ingredientId);
                if (!ingredient) return sum;
                return sum + (ingredient.costPerUnit * pi.quantity);
            }, 0);

            const costPerUnit = totalCost / Number(yieldQuantity);
            setUnitCost(costPerUnit.toFixed(2));
        }
    }, [useIngredients, productIngredients, yieldQuantity, ingredients]);

    const productPreview = {
        name,
        type,
        unitCost: Number(unitCost) || 0,
        marginPercent: Number(marginPercent) || 0,
    };

    const unitPrice = productPreview.unitCost > 0 && productPreview.marginPercent >= 0
        ? getProductPrice(productPreview)
        : 0;

    const calculatedPackagePrice = unitPrice * (Number(packageSize) || 1);
    const packagePrice = useCustomPackagePrice && customPackagePrice
        ? Number(customPackagePrice)
        : calculatedPackagePrice;

    const handleSelectIngredient = (ingredient) => {
        // Verifica se já está adicionado
        const existingIndex = productIngredients.findIndex(pi => pi.ingredientId === ingredient.id);

        if (existingIndex >= 0) {
            // Remove se já existe
            setProductIngredients(prev => prev.filter((_, i) => i !== existingIndex));
        } else {
            // Adiciona novo
            setProductIngredients(prev => [...prev, {
                ingredientId: ingredient.id,
                quantity: 1,
                unit: ingredient.unit
            }]);
        }
    };

    const handleIngredientQuantityChange = (ingredientId, quantity) => {
        setProductIngredients(prev => prev.map(pi =>
            pi.ingredientId === ingredientId
                ? { ...pi, quantity: Number(quantity) }
                : pi
        ));
    };

    const handleRemoveIngredient = (ingredientId) => {
        setProductIngredients(prev => prev.filter(pi => pi.ingredientId !== ingredientId));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!name.trim() || !unitCost) return;

        // Verificar limite de produtos apenas para novos cadastros
        if (!editingProduct && !canAddProduct(products.length)) {
            setShowUpgradeModal(true);
            return;
        }

        try {
            setSaving(true);
            const productData = {
                name: name.trim(),
                type,
                unitCost: Number(unitCost),
                marginPercent: Number(marginPercent),
                sellByPackage,
                packageSize: Number(packageSize) || 10,
                useCustomPackagePrice: sellByPackage ? useCustomPackagePrice : false,
                customPackagePrice: (sellByPackage && useCustomPackagePrice) ? Number(customPackagePrice) : null,
                packageTypes: packageTypes.filter(pt => pt.enabled),
                sellByCento,
                centoPrice: sellByCento ? Number(centoPrice) : null,
                useIngredients: type === 'artesanal' ? useIngredients : false,
                productIngredients: (type === 'artesanal' && useIngredients) ? productIngredients : [],
                yieldQuantity: (type === 'artesanal' && useIngredients) ? Number(yieldQuantity) : null,
            };

            if (editingProduct) {
                await updateProduct(editingProduct.id, productData);
                setSuccess('✓ Produto atualizado!');
                if (onEditComplete) onEditComplete();
            } else {
                await addProduct(productData);
                setName('');
                setUnitCost('');
                setMarginPercent('50');
                setType('artesanal');
                setSellByPackage(false);
                setPackageSize('10');
                setSellByCento(false);
                setCentoPrice('');
                if (nameRef.current) nameRef.current.focus();
                setSuccess('✓ Produto salvo com sucesso!');
            }

            setTimeout(() => setSuccess(''), 2500);
        } catch (err) {
            setError('Não foi possível salvar o produto. Tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (onEditComplete) onEditComplete();
        setName('');
        setUnitCost('');
        setMarginPercent('50');
        setType('artesanal');
        setSellByPackage(false);
        setPackageSize('10');
        setSellByCento(false);
        setCentoPrice('');
    };

    const currentPlan = userProfile?.plan || 'basico';
    const productLimit = getProductLimit();
    const isNearLimit = currentPlan === 'basico' && products.length >= productLimit * 0.8;
    const atLimit = currentPlan === 'basico' && products.length >= productLimit;

    return (
        <>
            <form onSubmit={handleSubmit} className="card shadow-sm">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <h2 className="h5 mb-0">
                            {editingProduct ? <><i className="bi bi-pencil me-2"></i>Editar Produto</> : <><i className="bi bi-box-seam me-2"></i>Cadastrar Produto</>}
                        </h2>
                        {!editingProduct && currentPlan === 'basico' && (
                            <span className={`badge ${atLimit ? 'bg-danger' : isNearLimit ? 'bg-warning' : 'bg-info'}`}>
                                {products.length}/{productLimit} produtos
                            </span>
                        )}
                    </div>

                    {!editingProduct && isNearLimit && !atLimit && (
                        <div className="alert alert-warning py-2 small mb-3">
                            <i className="bi bi-exclamation-triangle-fill me-1"></i>
                            Você está próximo do limite do plano Básico ({products.length}/{productLimit}).
                            Considere fazer upgrade para produtos ilimitados.
                        </div>
                    )}

                    {success && <div className="alert alert-success py-2 small">{success}</div>}
                    {error && <div className="alert alert-danger py-2 small">{error}</div>}

                <div className="mb-3">
                    <label className="form-label fw-semibold">Nome do produto</label>
                    <input
                        ref={nameRef}
                        type="text"
                        className="form-control"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ex: Mini pizza, Coxinha..."
                        required
                    />
                </div>

                <div className="mb-3">
                    <label className="form-label fw-semibold">Tipo de produto</label>
                    <select
                        className="form-select"
                        value={type}
                        onChange={(e) => {
                            setType(e.target.value);
                            if (e.target.value === 'congelado') {
                                setUseIngredients(false);
                            }
                        }}
                    >
                        <option value="artesanal">Artesanal (produção própria)</option>
                        <option value="congelado">Congelado (revenda)</option>
                    </select>
                </div>

                {/* Seção de ingredientes para produtos artesanais */}
                {type === 'artesanal' && (
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="useIngredients"
                                checked={useIngredients}
                                onChange={(e) => setUseIngredients(e.target.checked)}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="useIngredients">
                                Calcular custo baseado em matérias-primas
                            </label>
                        </div>
                        <div className="form-text small">
                            Ative para gerenciar ingredientes e calcular o custo automaticamente
                        </div>
                    </div>
                )}

                {type === 'artesanal' && useIngredients && (
                    <div className="mb-3 p-3 bg-light rounded border">
                        <h6 className="mb-3">Ingredientes e Rendimento</h6>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label fw-semibold small">Rendimento (unidades produzidas)</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    min="1"
                                    step="1"
                                    value={yieldQuantity}
                                    onChange={(e) => setYieldQuantity(e.target.value)}
                                    placeholder="Ex: 50"
                                    required={useIngredients}
                                />
                                <div className="form-text small">Quantas unidades esta receita produz</div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <IngredientManager
                                    onSelect={handleSelectIngredient}
                                    selectedIngredients={productIngredients}
                                />
                            </div>
                            <div className="col-md-6">
                                <h6 className="mb-2">Ingredientes Selecionados</h6>
                                {productIngredients.length === 0 ? (
                                    <div className="alert alert-info small mb-0">
                                        Selecione os ingredientes ao lado
                                    </div>
                                ) : (
                                    <div className="list-group list-group-flush">
                                        {productIngredients.map((pi) => {
                                            const ingredient = ingredients.find(i => i.id === pi.ingredientId);
                                            if (!ingredient) return null;

                                            return (
                                                <div key={pi.ingredientId} className="list-group-item">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <div className="fw-semibold small">{ingredient.name}</div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleRemoveIngredient(pi.ingredientId)}
                                                        >
                                                            <i className="bi bi-x"></i>
                                                        </button>
                                                    </div>
                                                    <div className="input-group input-group-sm">
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="0"
                                                            step="0.01"
                                                            value={pi.quantity}
                                                            onChange={(e) => handleIngredientQuantityChange(pi.ingredientId, e.target.value)}
                                                            required
                                                        />
                                                        <span className="input-group-text">{ingredient.unit}</span>
                                                        <span className="input-group-text">
                                                            R$ {(ingredient.costPerUnit * pi.quantity).toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <div className="list-group-item bg-primary bg-opacity-10">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="fw-bold small">Custo Total:</span>
                                                <span className="fw-bold">
                                                    R$ {productIngredients.reduce((sum, pi) => {
                                                        const ing = ingredients.find(i => i.id === pi.ingredientId);
                                                        return sum + (ing ? ing.costPerUnit * pi.quantity : 0);
                                                    }, 0).toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center mt-1">
                                                <span className="small text-muted">Custo por unidade:</span>
                                                <span className="fw-bold text-success">
                                                    R$ {unitCost || '0.00'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="row">
                    <div className="col-6 mb-3">
                        <label className="form-label fw-semibold">Custo unitário</label>
                        <div className="input-group">
                            <span className="input-group-text">R$</span>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="form-control"
                                value={unitCost}
                                onChange={(e) => setUnitCost(e.target.value)}
                                placeholder="0,00"
                                required
                                readOnly={useIngredients}
                                disabled={useIngredients}
                            />
                        </div>
                        <div className="form-text small">
                            {useIngredients ? 'Calculado automaticamente pelos ingredientes' : 'Custo por unidade'}
                        </div>
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label fw-semibold">Margem de lucro</label>
                        <div className="input-group">
                            <input
                                type="number"
                                min="0"
                                max="95"
                                step="1"
                                className="form-control"
                                value={marginPercent}
                                onChange={(e) => setMarginPercent(e.target.value)}
                            />
                            <span className="input-group-text">%</span>
                        </div>
                        <div className="form-text small">Lucro desejado</div>
                    </div>
                </div>

                {/* Seção de embalagens */}
                <div className="mb-3">
                    <div className="form-check form-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="sellByPackage"
                            checked={sellByPackage}
                            onChange={(e) => setSellByPackage(e.target.checked)}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="sellByPackage">
                            Vender também em bandejas/embalagens
                        </label>
                    </div>
                    <div className="form-text small">
                        Ative para vender individualmente ou em lotes (ex: bandeja com 10 unidades)
                    </div>
                </div>

                {sellByPackage && (
                    <div className="mb-3 p-3 bg-light rounded border">
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-semibold small">Quantidade por bandeja/embalagem</label>
                                <input
                                    type="number"
                                    min="2"
                                    step="1"
                                    className="form-control form-control-sm"
                                    value={packageSize}
                                    onChange={(e) => setPackageSize(e.target.value)}
                                    placeholder="10"
                                />
                                <div className="form-text small">
                                    Ex: 10 para bandejas com 10 unidades
                                </div>
                            </div>

                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-semibold small">Preço da bandeja</label>
                                <div className="input-group input-group-sm mb-2">
                                    <span className="input-group-text">R$</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="form-control"
                                        value={useCustomPackagePrice ? customPackagePrice : calculatedPackagePrice.toFixed(2)}
                                        onChange={(e) => {
                                            if (useCustomPackagePrice) {
                                                setCustomPackagePrice(e.target.value);
                                            }
                                        }}
                                        placeholder="0,00"
                                        readOnly={!useCustomPackagePrice}
                                        disabled={!useCustomPackagePrice}
                                    />
                                </div>
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="useCustomPackagePrice"
                                        checked={useCustomPackagePrice}
                                        onChange={(e) => {
                                            setUseCustomPackagePrice(e.target.checked);
                                            if (!e.target.checked) {
                                                setCustomPackagePrice('');
                                            } else {
                                                setCustomPackagePrice(calculatedPackagePrice.toFixed(2));
                                            }
                                        }}
                                    />
                                    <label className="form-check-label small" htmlFor="useCustomPackagePrice">
                                        Usar preço customizado
                                    </label>
                                </div>
                                {!useCustomPackagePrice && (
                                    <div className="form-text small text-success">
                                        Calculado: R$ {calculatedPackagePrice.toFixed(2)} ({packageSize} × R$ {unitPrice.toFixed(2)})
                                    </div>
                                )}
                                {useCustomPackagePrice && customPackagePrice && calculatedPackagePrice > 0 && (
                                    <div className="form-text small">
                                        {Number(customPackagePrice) < calculatedPackagePrice ? (
                                            <span className="text-warning">
                                                Desconto: R$ {(calculatedPackagePrice - Number(customPackagePrice)).toFixed(2)}
                                            </span>
                                        ) : Number(customPackagePrice) > calculatedPackagePrice ? (
                                            <span className="text-success">
                                                Adicional: R$ {(Number(customPackagePrice) - calculatedPackagePrice).toFixed(2)}
                                            </span>
                                        ) : (
                                            <span className="text-muted">Igual ao calculado</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Seção de venda por cento */}
                <div className="mb-3">
                    <div className="form-check form-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="sellByCento"
                            checked={sellByCento}
                            onChange={(e) => setSellByCento(e.target.checked)}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="sellByCento">
                            Vender também por cento (100 unidades)
                        </label>
                    </div>
                    <div className="form-text small">
                        Ative para vender em cento com preço especial diferente do unitário × 100
                    </div>
                </div>

                {sellByCento && (
                    <div className="mb-3 p-3 bg-light rounded border">
                        <label className="form-label fw-semibold small">Preço do cento (100 unidades)</label>
                        <div className="input-group input-group-sm">
                            <span className="input-group-text">R$</span>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="form-control"
                                value={centoPrice}
                                onChange={(e) => setCentoPrice(e.target.value)}
                                placeholder="0,00"
                                required={sellByCento}
                            />
                        </div>
                        <div className="form-text small">
                            Preço promocional para venda de 100 unidades
                        </div>
                    </div>
                )}

                {/* Preview de preços */}
                <div className="mb-3 p-3 bg-primary bg-opacity-10 rounded border border-primary">
                    <div className="small fw-semibold text-primary mb-2"><i className="bi bi-cash-coin me-2"></i>Preços calculados:</div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="small">Preço unitário:</span>
                        <span className="fw-bold">R$ {unitPrice.toFixed(2)}</span>
                    </div>
                    {sellByPackage && (
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="small">Preço da bandeja ({packageSize} un.):</span>
                            <span className="fw-bold text-success">R$ {packagePrice.toFixed(2)}</span>
                        </div>
                    )}
                    {sellByCento && (
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="small">Preço do cento (100 un.):</span>
                            <span className="fw-bold text-success">R$ {(Number(centoPrice) || 0).toFixed(2)}</span>
                        </div>
                    )}
                    <div className="form-text small mt-2">
                        {sellByCento
                            ? 'Preço unitário e bandeja calculados automaticamente. Preço do cento é personalizado.'
                            : 'Valores calculados automaticamente com base no custo e margem'
                        }
                    </div>
                </div>

                <div className="d-flex gap-2">
                    <button
                        type="submit"
                        className="btn btn-primary flex-grow-1"
                        disabled={saving}
                    >
                        {saving ? <><i className="bi bi-hourglass-split me-2"></i>Salvando...</> : editingProduct ? <><i className="bi bi-check-circle me-2"></i>Atualizar</> : <><i className="bi bi-check-circle me-2"></i>Salvar produto</>}
                    </button>
                    {editingProduct && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCancel}
                        >
                            Cancelar
                        </button>
                    )}
                </div>
            </div>
        </form>

        <UpgradeModal
            show={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            feature="products"
        />
        </>
    );
}
